import { AFFIXES, ARMORS, CHARMS, WEAPONS, LEGENDARIES, type ItemInst, type ItemDef, type Rarity, type WeaponClass } from '../content/items';
import { HEROES } from '../content/heroes';
import { CONSUMABLES, CONSUMABLE_IDS } from '../content/consumables';
import type { Modifier } from '../content/stats';
import { Rng } from '../rng';
import type { World } from './world';
import type { MobEnt, Pickup, PlayerEnt } from './types';
import { addConsumable, addXp, takeItem } from './player';
import { heal } from './combat';

export function rollRarity(rng: Rng, luck: number): Rarity {
  const common = Math.max(10, 62 - luck * 4);
  const rare = 30 + luck * 2.5;
  const epic = 8 + luck * 1.5;
  const i = rng.weightedIndex([common, rare, epic]);
  return i as Rarity;
}

/** Keeps only the weapons the hero was trained for; armour and charms pass through. */
function usable<T extends ItemDef>(list: T[], classes?: WeaponClass[]): T[] {
  if (!classes) return list;
  const out = list.filter((d) => d.slot !== 'weapon' || classes.includes(d.wclass));
  return out.length ? out : list;
}

/** A legendary of a random slot with one or two modest affixes; the perk is the point. */
export function rollLegendary(rng: Rng, uid: number, classes?: WeaponClass[]): ItemInst {
  const slotRoll = rng.next();
  const slot = slotRoll < 0.36 ? 'weapon' : slotRoll < 0.7 ? 'armor' : 'charm';
  const pool = usable(Object.values(LEGENDARIES).filter((d) => d.slot === slot), classes);
  const def = pool[rng.int(0, pool.length - 1)] ?? Object.values(LEGENDARIES)[0];
  const affixes: Modifier[] = [];
  const n = rng.int(1, 2);
  const used = new Set<number>();
  for (let i = 0; i < n; i++) {
    let idx = rng.int(0, AFFIXES.length - 1);
    let tries = 0;
    while (used.has(idx) && tries++ < 10) idx = rng.int(0, AFFIXES.length - 1);
    used.add(idx);
    const t = AFFIXES[idx];
    const v = t.min + (t.max - t.min) * rng.float(0.6, 1);
    const rounded = t.kind === 'add' ? Math.round(v * 10) / 10 : Math.round(v * 100) / 100;
    affixes.push(t.kind === 'add' ? { stat: t.stat, add: rounded } : { stat: t.stat, inc: rounded });
  }
  return { uid, def: def.id, rarity: 3, affixes };
}

export function rollItem(rng: Rng, tier: number, luck: number, uid: number, minRarity: Rarity = 0, legendaryChance = 0, classes?: WeaponClass[]): ItemInst {
  if (legendaryChance > 0 && rng.chance(Math.min(0.5, legendaryChance))) return rollLegendary(rng, uid, classes);
  const slotRoll = rng.next();
  const pool = slotRoll < 0.36 ? usable(Object.values(WEAPONS), classes) : slotRoll < 0.7 ? Object.values(ARMORS) : Object.values(CHARMS);
  const cands = pool.filter((d) => d.tier <= tier);
  const weights = cands.map((d) => (d.tier === tier ? 3 : 1));
  const def = cands[rng.weightedIndex(weights)];
  const rarity = Math.min(2, Math.max(minRarity, rollRarity(rng, luck))) as Rarity;
  const affixCount = rarity === 0 ? 0 : rarity === 1 ? rng.int(1, 2) : rng.int(2, 3);
  const affixes: Modifier[] = [];
  const used = new Set<number>();
  for (let i = 0; i < affixCount; i++) {
    let idx = rng.int(0, AFFIXES.length - 1);
    let tries = 0;
    while (used.has(idx) && tries++ < 10) idx = rng.int(0, AFFIXES.length - 1);
    used.add(idx);
    const t = AFFIXES[idx];
    const q = rarity === 2 ? rng.float(0.6, 1) : rng.float(0.3, 0.85);
    const v = t.min + (t.max - t.min) * q;
    const rounded = t.kind === 'add' ? Math.round(v * 10) / 10 : Math.round(v * 100) / 100;
    affixes.push(t.kind === 'add' ? { stat: t.stat, add: rounded } : { stat: t.stat, inc: rounded });
  }
  return { uid, def: def.id, rarity, affixes };
}

export function rollConsumable(rng: Rng): string {
  const ids = CONSUMABLE_IDS;
  const weights = ids.map((id) => CONSUMABLES[id].weight);
  return ids[rng.weightedIndex(weights)];
}

export function spawnPickup(world: World, x: number, y: number, o: { type: Pickup['type']; item?: ItemInst; consumable?: string; amount?: number; owner: number; ttl: number }): Pickup {
  const id = world.allocId();
  const pos = world.map.nearestWalkable(x, y, 10, 6);
  const p: Pickup = { id, x: pos.x, y: pos.y, r: 12, type: o.type, item: o.item, consumable: o.consumable, amount: o.amount ?? 0, owner: o.owner, until: world.tick + world.seconds(o.ttl) };
  world.pickups.set(id, p);
  return p;
}

export function dropLoot(world: World, m: MobEnt, killer: PlayerEnt | null): void {
  const rng = world.rng;
  const luck = killer ? killer.stats.luck : 0;
  // a dropped weapon should be one the finder could actually swing
  const classes = killer ? HEROES[killer.heroId].weapons : undefined;
  const dbl = killer?.flags.has('lootDouble') ?? false;
  const tier = Math.max(1, Math.min(3, m.tier));
  if (m.kind === 'boss') {
    for (const p of world.players.values()) {
      if (p.dead) continue;
      spawnPickup(world, m.x + rng.float(-60, 60), m.y + rng.float(-60, 60), { type: 'item', item: rollItem(rng, 3, p.stats.luck + 2, world.allocId(), 2, 0.2 + p.stats.luck * 0.02, HEROES[p.heroId].weapons), owner: p.id, ttl: 120 });
      spawnPickup(world, m.x + rng.float(-60, 60), m.y + rng.float(-60, 60), { type: 'consumable', consumable: rollConsumable(rng), owner: p.id, ttl: 120 });
      spawnPickup(world, m.x + rng.float(-60, 60), m.y + rng.float(-60, 60), { type: 'consumable', consumable: rollConsumable(rng), owner: p.id, ttl: 120 });
    }
    spawnPickup(world, m.x, m.y, { type: 'item', item: rollItem(rng, 3, 2, world.allocId(), 1, 0, classes), owner: 0, ttl: 120 });
    return;
  }
  if (m.kind === 'revenant') {
    spawnPickup(world, m.x, m.y, { type: 'item', item: rollItem(rng, tier, luck + 1, world.allocId(), 1, 0, classes), owner: 0, ttl: 90 });
    spawnPickup(world, m.x + 20, m.y, { type: 'consumable', consumable: rollConsumable(rng), owner: 0, ttl: 60 });
    return;
  }
  if (m.elite) {
    spawnPickup(world, m.x, m.y, { type: 'item', item: rollItem(rng, tier, luck, world.allocId(), 0, tier >= 3 ? 0.03 + luck * 0.004 : 0, classes), owner: 0, ttl: 90 });
    if (dbl || rng.chance(0.5)) spawnPickup(world, m.x + 18, m.y, { type: 'consumable', consumable: rollConsumable(rng), owner: 0, ttl: 60 });
    return;
  }
  const itemChance = (0.02 + tier * 0.012) * (dbl ? 2 : 1) * (1 + luck * 0.15);
  if (rng.chance(itemChance)) spawnPickup(world, m.x, m.y, { type: 'item', item: rollItem(rng, tier, luck, world.allocId(), 0, 0, classes), owner: 0, ttl: 90 });
  else if (rng.chance(0.06 * (dbl ? 2 : 1))) spawnPickup(world, m.x, m.y, { type: 'consumable', consumable: rollConsumable(rng), owner: 0, ttl: 60 });
  else if (rng.chance(0.05)) spawnPickup(world, m.x, m.y, { type: 'heal', amount: 0.2, owner: 0, ttl: 45 });
}

export function stepPickups(world: World): void {
  for (const [id, pk] of world.pickups) {
    if (world.tick >= pk.until) {
      world.pickups.delete(id);
      continue;
    }
    for (const p of world.players.values()) {
      if (p.dead) continue;
      if (pk.owner && pk.owner !== p.id) continue;
      const rr = p.stats.pickupRadius + pk.r;
      const dx = p.x - pk.x;
      const dy = p.y - pk.y;
      if (dx * dx + dy * dy > rr * rr) continue;
      if (pk.type === 'item' && pk.item) {
        if (!takeItem(world, p, pk.item)) continue;
      } else if (pk.type === 'consumable' && pk.consumable) {
        if (!addConsumable(p, pk.consumable)) continue;
        world.events.push({ t: 'pickup', id: p.id, type: 'consumable', what: pk.consumable });
      } else if (pk.type === 'heal') {
        if (p.hp >= p.maxHp) continue;
        heal(world, p, p.maxHp * pk.amount, null);
        world.events.push({ t: 'pickup', id: p.id, type: 'heal' });
      } else if (pk.type === 'xp') {
        addXp(world, p, pk.amount);
        world.events.push({ t: 'pickup', id: p.id, type: 'xp', amount: pk.amount });
      }
      world.pickups.delete(id);
      break;
    }
  }
}
